import React, {ReactNode, useMemo} from "react"
import {Button} from "semantic-ui-react"
import {I18nIndividual as GedcomIndividual} from "../model/gedcom-individual"
import {I18nFamily as GedcomFamily} from "../model/gedcom-family"
import {FormattedMessage} from "react-intl"

const ENTITY_MESSAGES = new Map<Function, ReactNode>([
    [GedcomIndividual, <FormattedMessage id="creator.constraints.entity.individual"/>],
    [GedcomFamily, <FormattedMessage id="creator.constraints.entity.family"/>]
])

interface ConfirmRemoveProps<T extends GedcomIndividual | GedcomFamily> {
    rowType: typeof GedcomIndividual | typeof GedcomFamily
    row: T
    individualsData: GedcomIndividual[]
    familiesData?: GedcomFamily[]
    childrenMap: Record<string, GedcomIndividual[]>
    spousesMap: Record<string, GedcomFamily[]>
    fullNames: Record<string, string>
    onConfirmRemove: () => void
}

const literalKey = (rowType: string) => rowType.replace("I18n", "").toLowerCase()

const joinWithCommas = (nodes: ReactNode[]) =>
    <>{nodes.map((node, idx) =>
        <React.Fragment key={idx}>{node}{idx < nodes.length - 1 ? ", " : ""}</React.Fragment>)
    }</>

export const ConfirmRemove = <T extends GedcomIndividual | GedcomFamily>({
    rowType,
    row,
    childrenMap,
    spousesMap,
    fullNames,
    onConfirmRemove
}: ConfirmRemoveProps<T>) => {

    const linkedDescendants = useMemo(() => childrenMap[row.id] ?? [], [childrenMap, row.id])
    const linkedSpouses = useMemo(() => spousesMap[row.id] ?? [], [spousesMap, row.id])

    const safeDelete = useMemo(() =>
        (rowType === GedcomIndividual && linkedDescendants.length === 0 && linkedSpouses.length === 0) ||
        (rowType === GedcomFamily && (row as GedcomFamily).children.size === 0),
    [row, rowType, linkedDescendants.length, linkedSpouses.length])

    const renderChildren = useMemo(() => {
        return (childrenIds: string[]) => joinWithCommas(childrenIds.map(childId => (
            <span key={childId} style={{ fontWeight: "bold", color: "var(--creator-indi-yellow-active)" }}>{fullNames[childId] ?? childId}</span>
        )))
    }, [fullNames])

    const renderFamilies = useMemo(() => {
        return (familyIds: string[]) => joinWithCommas(familyIds.map(familyId => (
            <span key={familyId} style={{ fontWeight: "bold", color: "var(--creator-fam-teal-active)" }}>{familyId}</span>
        )))
    }, [])

    return (
        <div style={{ textAlign: "center" }}>
            {(() => {
                if (rowType === GedcomIndividual) {
                    if (linkedDescendants.length > 0) {
                        return <>
                            <p><FormattedMessage id="creator.constraints.deps.numberOfChildren" values={{
                                entity: <FormattedMessage id="creator.constraints.entity.individual"/>,
                                nChildren: <span style={{ fontWeight: "bold" }}>{(row as GedcomIndividual).numberOfChildren}</span>,
                                children: renderChildren(linkedDescendants.map(child => child.id))
                            }}/></p>
                            <p><FormattedMessage id={`creator.constraints.consider`}/></p>
                        </>
                    }
                    if (linkedSpouses.length > 0) {
                        return <>
                            <p><FormattedMessage id="creator.constraints.deps.numberOfMarriages" values={{
                                entity: <FormattedMessage id="creator.constraints.entity.individual"/>,
                                numberOfMarriages: <span style={{ fontWeight: "bold" }}>{(row as GedcomIndividual).numberOfMarriages}</span>,
                                families: renderFamilies(linkedSpouses.map(f => f.id))
                            }}/></p>
                            <p><FormattedMessage
                                id={`creator.constraints.${literalKey(row.constructor.name)}.consider`}
                                values={{ entity: ENTITY_MESSAGES.get(row.constructor) }}
                            /></p>
                        </>
                    }
                }
                if (rowType === GedcomFamily && (row as GedcomFamily).children?.size > 0) {
                    return <>
                        <p><FormattedMessage id="creator.constraints.deps.children" values={{
                            entity: <FormattedMessage id="creator.constraints.entity.family"/>,
                            nChildren: <span style={{ fontWeight: "bold" }}>{(row as GedcomFamily).children.size}</span>,
                            children: renderChildren(Array.from((row as GedcomFamily).children))
                        }}/></p>
                        <p><FormattedMessage id={`creator.constraints.consider`} values={{ entity: ENTITY_MESSAGES.get(row.constructor) }}/></p>
                    </>
                }
                return <>
                    <p><FormattedMessage
                        id={`creator.constraints.${literalKey(row.constructor.name)}.fine`}
                        values={{ entity: ENTITY_MESSAGES.get(row.constructor) }}
                    /></p>
                    <p><FormattedMessage id="creator.constraints.disable.toggle"/></p>
                </>
            })()}

            <Button
                color={safeDelete ? "green" : "red"}
                size="small"
                onClick={onConfirmRemove}
                content={<FormattedMessage id="creator.constraints.remove.confirm"/>}
            />
        </div>
    )
}
